import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { toast } from "./ui/sonner";
import { Settings2, X, Loader2 } from "lucide-react";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";

export default function ReminderPrefs() {
  const { lang } = useApp();
  const zh = lang === "zh";
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && !p) api.get("/me/reminder-prefs").then(({ data }) => setP(data)).catch(() => setP({ lowFollowupEnabled: true, lowScoreThreshold: 40, staleDays: 7, reminderHour: 8 }));
  }, [open, p]);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/me/reminder-prefs", p); setP(data); toast.success(zh ? "提醒偏好已保存" : "Preferences saved"); setOpen(false); }
    catch { toast.error(zh ? "保存失败" : "Save failed"); }
    setSaving(false);
  };

  return (
    <>
      <button data-testid="reminder-prefs-btn" onClick={() => setOpen(true)} className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg px-3 py-1.5 transition-colors">
        <Settings2 className="h-4 w-4" />{zh ? "提醒设置" : "Reminder settings"}
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="reminder-prefs-modal" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-800">{zh ? "跟进提醒偏好" : "Follow-up reminder preferences"}</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-red-600"><X className="h-5 w-5" /></button>
            </div>
            {!p ? <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div> : (
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{zh ? "低分跟进提醒" : "Low-score reminders"}</p>
                    <p className="text-[11px] text-slate-400">{zh ? "客户跟进评分偏低且长期无推进时提醒我" : "Remind me when a lead is low-scoring & stale"}</p>
                  </div>
                  <Switch data-testid="pref-enabled" checked={p.lowFollowupEnabled} onCheckedChange={(v) => setP({ ...p, lowFollowupEnabled: v })} />
                </div>
                <div className={p.lowFollowupEnabled ? "space-y-4" : "space-y-4 opacity-40 pointer-events-none"}>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm text-slate-600">{zh ? "评分低于" : "Score below"}</label>
                      <span className="text-sm font-semibold text-red-600" data-testid="pref-threshold-val">{p.lowScoreThreshold}</span>
                    </div>
                    <input data-testid="pref-threshold" type="range" min="0" max="100" step="5" value={p.lowScoreThreshold} onChange={(e) => setP({ ...p, lowScoreThreshold: Number(e.target.value) })} className="w-full accent-brand-500" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm text-slate-600">{zh ? "无推进天数 ≥" : "Days without progress ≥"}</label>
                      <span className="text-sm font-semibold text-slate-700" data-testid="pref-staledays-val">{p.staleDays}</span>
                    </div>
                    <input data-testid="pref-staledays" type="range" min="0" max="30" step="1" value={p.staleDays} onChange={(e) => setP({ ...p, staleDays: Number(e.target.value) })} className="w-full accent-slate-600" />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-slate-600">{zh ? "每日提醒时间" : "Daily reminder hour"}</label>
                    <select data-testid="pref-hour" value={p.reminderHour} onChange={(e) => setP({ ...p, reminderHour: Number(e.target.value) })} className="text-sm border border-slate-200 rounded-md px-2 py-1.5">
                      {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, "0")}:00</option>)}
                    </select>
                  </div>
                </div>
                <Button data-testid="pref-save" onClick={save} disabled={saving} className="w-full bg-brand-500 hover:bg-brand-600 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{zh ? "保存" : "Save"}</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
